import { useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import SecureStorageService from '../services/secureStorageService';

/**
 * Custom hook to access user roles with fallback strategy
 * 
 * Priority:
 * 1. Token claims (most reliable, always current)
 * 2. SecureStorageService (cached, survives page refreshes)
 * 3. Empty array (unauthenticated)
 * 
 * @returns {Array<string>} Array of role strings
 * 
 * @example
 * const MyComponent = () => {
 *   const roles = useUserRoles();
 *   const isAdmin = roles.includes('Admin');
 *   const isPartnerAdmin = roles.includes('PartnerAdmin');
 *   
 *   return (
 *     <div>
 *       {isAdmin && <AdminSection />}
 *       {isPartnerAdmin && <PartnerSection />}
 *     </div>
 *   );
 * };
 */
export const useUserRoles = () => {
  const { accounts } = useMsal();
  
  return useMemo(() => {
    // Try to get roles from token claims first (most reliable)
    const currentAccount = accounts[0];
    
    if (currentAccount?.idTokenClaims?.extension_Role) {
      const tokenRoles = currentAccount.idTokenClaims.extension_Role
        .split(',')
        .map(role => role.trim())
        .filter(role => role.length > 0);
      
      if (tokenRoles.length > 0) {
        // Also update SecureStorageService cache for consistency
        SecureStorageService.setRoles(tokenRoles);
        return tokenRoles;
      }
    }
    
    // Fallback to SecureStorageService (for components that load before MSAL)
    const storedRoles = SecureStorageService.getRoles();
    if (storedRoles && storedRoles.length > 0) {
      return storedRoles;
    }
    
    // No roles available
    return [];
  }, [accounts]);
};

/**
 * Hook to check if user has specific role(s)
 * 
 * @param {...string} requiredRoles - Role(s) to check
 * @returns {boolean} True if user has any of the required roles
 * 
 * @example
 * const MyComponent = () => {
 *   const isAdmin = useHasRole('Admin');
 *   const canManage = useHasRole('Admin', 'PartnerAdmin');
 *   
 *   return canManage ? <ManagementPanel /> : <AccessDenied />;
 * };
 */
export const useHasRole = (...requiredRoles) => {
  const userRoles = useUserRoles();
  
  return useMemo(() => {
    if (requiredRoles.length === 0) return true;
    return requiredRoles.some(role => userRoles.includes(role));
  }, [userRoles, requiredRoles]);
};

/**
 * Hook to check if user has all specified roles
 * 
 * @param {...string} requiredRoles - Roles to check
 * @returns {boolean} True only if user has ALL required roles
 * 
 * @example
 * const MyComponent = () => {
 *   const hasAllRoles = useHasAllRoles('Admin', 'PartnerAdmin');
 *   
 *   return hasAllRoles ? <SuperAdminPanel /> : null;
 * };
 */
export const useHasAllRoles = (...requiredRoles) => {
  const userRoles = useUserRoles();
  
  return useMemo(() => {
    if (requiredRoles.length === 0) return true;
    return requiredRoles.every(role => userRoles.includes(role));
  }, [userRoles, requiredRoles]);
};

/**
 * Hook to get user's primary role (highest privilege)
 * 
 * Hierarchy: Admin > PartnerAdmin > User
 * 
 * @returns {string|null} Primary role or null if no roles
 * 
 * @example
 * const MyComponent = () => {
 *   const primaryRole = usePrimaryRole();
 *   
 *   switch (primaryRole) {
 *     case 'Admin':
 *       return <AdminDashboard />;
 *     case 'PartnerAdmin':
 *       return <PartnerDashboard />;
 *     default:
 *       return <UserDashboard />;
 *   }
 * };
 */
export const usePrimaryRole = () => {
  const userRoles = useUserRoles();
  
  return useMemo(() => {
    if (userRoles.includes('Admin')) return 'Admin';
    if (userRoles.includes('PartnerAdmin')) return 'PartnerAdmin';
    if (userRoles.includes('User')) return 'User';
    return null;
  }, [userRoles]);
};

/**
 * Hook to check authentication status
 * 
 * @returns {boolean} True if user is authenticated
 * 
 * @example
 * const MyComponent = () => {
 *   const isAuthenticated = useIsAuthenticated();
 *   
 *   return isAuthenticated ? <Dashboard /> : <LoginPrompt />;
 * };
 */
export const useIsAuthenticated = () => {
  const { accounts } = useMsal();
  return accounts && accounts.length > 0;
};
